import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Home } from 'lucide-react'
import { useLanguage } from '@/localization'

/**
 * Cross-property "network switcher" shown in the nav. The Uliunai sites are
 * separate SPAs on separate subdomains, so links are plain cross-origin <a>
 * tags (not router links). `currentId` highlights the active property.
 */
interface NetworkProperty {
  id: string
  name: string
  /** Live destination. Undefined ⇒ "coming soon" (not yet linkable). */
  url?: string
  status: 'live' | 'soon'
}

const PROPERTIES: NetworkProperty[] = [
  { id: 'kf1', name: 'Killing Floor', url: 'https://kf1.uliunai.com', status: 'live' },
  { id: 'minecraft', name: 'Minecraft', url: 'https://mc.uliunai.com', status: 'live' },
  { id: 'kf2', name: 'Killing Floor 2', status: 'soon' },
  { id: 'cs16', name: 'Counter-Strike 1.6', status: 'soon' },
  { id: 'cs2', name: 'Counter-Strike 2', status: 'soon' },
  { id: 'l4d2', name: 'Left 4 Dead 2', status: 'soon' },
]

const HUB_URL = 'https://uliunai.com'

export default function GameSwitcher({ currentId }: { currentId: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { t } = useLanguage()

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('Switch Uliunai game')}
        className="flex items-center gap-1 rounded-lg px-1.5 py-1 hover:bg-white/10 transition-colors cursor-pointer"
      >
        <img src="/brand/ul-logo.png" alt="Uliunai" className="w-10 h-10 object-contain drop-shadow" />
        <ChevronDown
          size={16}
          className={`text-white/50 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-72 rounded-xl border border-grass-dark/60 bg-slate-deep/95 backdrop-blur-md shadow-block p-3 z-50">
          <div className="px-1 pb-2 mb-2 border-b border-white/10 font-pixel text-[10px] text-grass">
            {t('ULIUNAI NETWORK')}
          </div>

          <div className="grid grid-cols-2 gap-1.5">
            {PROPERTIES.map((p) => {
              const isCurrent = p.id === currentId
              const inner = (
                <>
                  <span className="flex items-center gap-2 min-w-0">
                    <span
                      className={`w-1.5 h-1.5 rounded-full shrink-0 ${p.status === 'live' ? 'bg-grass shadow-[0_0_6px] shadow-grass/70' : 'bg-white/25'}`}
                    ></span>
                    <span className="truncate">{p.name}</span>
                  </span>
                  {p.status === 'soon' && (
                    <span className="text-[10px] uppercase tracking-wide text-white/30">{t('Soon')}</span>
                  )}
                </>
              )
              const cls = `flex items-center justify-between gap-1 rounded-lg px-2.5 py-2 text-sm font-bold transition-colors ${
                isCurrent
                  ? 'bg-grass-dark/40 text-white ring-1 ring-grass/50'
                  : p.url
                    ? 'text-white/75 hover:bg-white/10 hover:text-white cursor-pointer'
                    : 'text-white/35 cursor-default'
              }`

              if (p.url && !isCurrent) {
                return (
                  <a key={p.id} href={p.url} className={cls}>
                    {inner}
                  </a>
                )
              }
              return (
                <div key={p.id} className={cls} aria-current={isCurrent ? 'page' : undefined}>
                  {inner}
                </div>
              )
            })}
          </div>

          <a
            href={HUB_URL}
            className="mt-2 flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-bold text-white/80 hover:bg-white/10 hover:text-white transition-colors border-t border-white/10 pt-3"
          >
            <Home size={16} />
            {t('Uliunai Hub')}
          </a>
        </div>
      )}
    </div>
  )
}
